import { useEffect, useState } from "react";
import { SMALL_VIEWPORT_MAX_WIDTH } from "config/constants";
import { ActionIcon, Container, Grid, Group, Stack, Text } from "@mantine/core";
import { BicycleType, Routing } from "config/enums/routings";
import { BICYCLE_ICONS, ROUTING_ICONS } from "config/icons";
import { useAuth } from "hooks/useAuth";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useRouting } from "stores/routingStore/RoutingContext";
import { BicycleCostingOptions } from "types/app";
import { ActionIconGroup } from "./ActionIconGroup";
import { SliderWithLabel } from "./SliderWithLabel";
import "./RoutingPicker.css";
import { showWarningNotification } from "utils/notifications/customNotifications";
import choiceClasses from "components/UI/ChoiceActionIcon.module.css";
import classes from "./RoutingPicker.module.css";

const getRoutingItems = (t: TFunction) => [
  {
    icon: ROUTING_ICONS.bicycle,
    label: t("general.routing.bike"),
    value: Routing.bicycle,
  },
  {
    icon: ROUTING_ICONS.auto,
    label: t("general.routing.car"),
    value: Routing.auto,
  },
  {
    icon: ROUTING_ICONS.pedestrian,
    label: t("general.routing.walk"),
    value: Routing.pedestrian,
  },
  {
    icon: ROUTING_ICONS.none,
    label: t("general.routing.none"),
    value: Routing.none,
  },
];

const getBicycleTypeItems = (t: TFunction) => [
  {
    icon: BICYCLE_ICONS.Hybrid,
    label: t("general.bicycle_costing_options.hybrid_icon_label"),
    value: BicycleType.hybrid,
  },
  {
    icon: BICYCLE_ICONS.Road,
    label: t("general.bicycle_costing_options.road_icon_label"),
    value: BicycleType.road,
  },
  {
    icon: BICYCLE_ICONS.Cross,
    label: t("general.bicycle_costing_options.crossCountry_icon_label"),
    value: BicycleType.crossCountry,
  },
  {
    icon: BICYCLE_ICONS.Mountain,
    label: t("general.bicycle_costing_options.mountain_icon_label"),
    value: BicycleType.mountain,
  },
];

export const RoutingPicker = () => {
  const { state, changeRouting, updateCostingOptions } = useRouting();
  const { user, update } = useAuth();
  const { t } = useTranslation();
  const [width, setWindowWidth] = useState(0);
  // Larger buttons and texts outside the small screen layout
  const isWide = width > SMALL_VIEWPORT_MAX_WIDTH;
  const [isOptionsPanelOpen, setOptionsPanelOpen] = useState(false);

  const updateDimensions = () => {
    const { innerWidth } = window;
    setWindowWidth(innerWidth);
  };

  useEffect(() => {
    updateDimensions();

    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  useEffect(() => {
    // This handles use cases when routing is switched outside of Routing picker (e.g. from Routes tab)
    setOptionsPanelOpen((value) => {
      if (!value && state.routing === Routing.bicycle) {
        return true;
      }
      if (value && state.routing !== Routing.bicycle) return false;
      return value;
    });
  }, [state.routing]);

  return (
    <>
      <Group pt="xs" pb="xs" className="trkbk-routing-panel" gap="xs">
        {getRoutingItems(t).map(({ value, icon, label }) => {
          const isSelected = value === state.routing;

          return (
            <ActionIcon
              key={label}
              onClick={() => {
                // We need to check here if we are not trying to change routing to auto that has
                // max track point limit of 20 (it would cause a crash on the backend)
                if (
                  value === Routing.auto &&
                  state.trackWaypoints.length > 20
                ) {
                  showWarningNotification({
                    message: t(
                      "notifications.routing.unable_to_change_routing_track_point_limit.message"
                    ),
                    title: t(
                      "notifications.routing.unable_to_change_routing_track_point_limit.title"
                    ),
                  });
                  return;
                }

                if (
                  value === Routing.bicycle &&
                  state.routing !== Routing.none
                ) {
                  setOptionsPanelOpen((value_) => !value_);
                } else if (isOptionsPanelOpen) {
                  setOptionsPanelOpen(false);
                }

                changeRouting(value);
                if (user) void update({ routing: value });
              }}
              variant={isSelected ? "filled" : "transparent"}
              size={isWide ? 72 : 52}
              className={choiceClasses.choice}
              mod={{ selected: isSelected }}
              style={{ fontSize: isWide ? 26 : 16 }}
            >
              <Stack gap="xs" style={{ overflow: "hidden", padding: "5px" }}>
                {value === Routing.bicycle
                  ? BICYCLE_ICONS[
                      state.costingOptions[Routing.bicycle].bicycleType
                    ]
                  : icon}
                <Text
                  inline
                  style={{
                    fontSize: isWide ? "0.9rem" : "0.8rem",
                    textAlign: "center",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                  }}
                  c={isSelected ? "white" : ""}
                >
                  {label}
                </Text>
              </Stack>
            </ActionIcon>
          );
        })}
      </Group>
      {isOptionsPanelOpen ? (
        <Container
          className={`trkbk-routing-options-panel ${classes.optionsPanel}`}
          p={isWide ? 15 : 12}
          m={0}
          mt={5}
        >
          <Grid align="start" justify="start" gap={5} grow>
            <Grid.Col span={2}>
              <ActionIconGroup
                iconSize={isWide ? "1.8rem" : "1.0rem"}
                items={getBicycleTypeItems(t)}
                labelFontSize={isWide ? "0.8rem" : "0.5rem"}
                onClick={(value) =>
                  updateCostingOptions({
                    [Routing.bicycle]: {
                      bicycleType:
                        value as BicycleCostingOptions["bicycleType"],
                    },
                  })
                }
                size={isWide ? 60 : 40}
                value={state.costingOptions[Routing.bicycle].bicycleType}
              />
              <div className="slider-container">
                <SliderWithLabel
                  label={t(
                    "general.bicycle_costing_options.use_hills_slider_label"
                  )}
                  labelFontSize={isWide ? "0.8em" : "0.7em"}
                  onChange={(value) =>
                    updateCostingOptions({
                      [Routing.bicycle]: {
                        useHills: String(
                          value
                        ) as BicycleCostingOptions["useHills"],
                      },
                    })
                  }
                  markLabelFontSize={isWide ? "0.7em" : "0.5em"}
                  markTooltip={null}
                  marks={[
                    { value: 0, label: "0%" },
                    { value: 0.5, label: "50%" },
                    { value: 1, label: "100%" },
                  ]}
                  min={0}
                  max={1}
                  step={0.5}
                  value={Number(state.costingOptions[Routing.bicycle].useHills)}
                  thumbSize={isWide ? 18 : 16}
                />
              </div>
            </Grid.Col>
          </Grid>
        </Container>
      ) : null}
    </>
  );
};
